/**
 * templateFitPolicy.ts — the two-tier candidate split and the fill-mode rule
 * that follows from it.
 *
 * `/slides/transform-retrieve` returns candidates ranked best-first. The picker
 * shows the leading {@link BEST_MATCH_COUNT} as "Best matches" with their match
 * %, and the remainder as "More layouts".
 *
 * The split is not only cosmetic. A layout from the lower tier was NOT matched
 * to this slide's structure, so preserving the user's wording verbatim would
 * routinely overflow or leave slots empty. Such a pick therefore forces
 * `"generic"` — the mockup's "browse picks force AI rewrite" rule.
 *
 * It lives here, rather than in the picker, because two surfaces now apply it:
 * the Office Dialog that owns selection, and the in-pane fallback used when the
 * host has no dialog. One definition keeps them from drifting into disagreeing
 * about what a given set of picks will actually do.
 */

import type { TemplateCandidate, TemplateFitMode } from "./slideTransformTemplateApi";

/** How many of the ranked candidates count as "best matches". */
export const BEST_MATCH_COUNT = 6;

/**
 * True when any pick sits below the best-matches tier.
 *
 * Unknown ids (not in `candidates`) count as lower-tier: an id the ranker never
 * returned certainly wasn't matched to this slide, and the safe reading of an
 * unmatched pick is the one that reshapes content rather than truncating it.
 */
export function hasLowerTierPick(
  picks: readonly string[],
  candidates: readonly TemplateCandidate[],
): boolean {
  return picks.some((id) => {
    const index = candidates.findIndex((candidate) => candidate.id === id);
    return index < 0 || index >= BEST_MATCH_COUNT;
  });
}

/**
 * The fill mode that will actually be used — `chosen`, unless a lower-tier pick
 * forces `"generic"`.
 *
 * Callers should render this rather than the raw choice, so the control never
 * claims a mode the run won't honour.
 */
export function resolveFitMode(
  picks: readonly string[],
  candidates: readonly TemplateCandidate[],
  chosen: TemplateFitMode,
): TemplateFitMode {
  return hasLowerTierPick(picks, candidates) ? "generic" : chosen;
}
